import React, { useEffect } from 'react'
import { DashboardSectionType } from '../../../../../Model/Enum/DashboardType';
import { Box, Button, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { WSPDashboardService } from '../../../../../Api/WSPDashboard/WSPDashboardService';
import { SmallLoading } from '../../../Loading/IntermediateLoading';
import { WorkforceProfileOFOCodeSummaryModal } from './WorkforceProfileOFOCodeSummaryModal';
import { useHistory } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { WSPScorecardAtom } from '../../../../../../State/WSPScorecard.Atom';
import { WorkforceProfileOFOCodeSummary } from '../../../../../Model/Dto/WSP/WorkforceProfileOFOCodeSummary';
import { formControlColour, tooltipColour } from '../../../BEE123.Constants';
import moment from 'moment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { WorkforceProfileService } from '../../../../../Api/WorkforceProfile/WorkforceProfileService';


interface IProps {
    scorecardId?: string;
    scorecardTypeId?: string;
    chartOptions?: any;
    dashboardType?: DashboardSectionType;
}

export const WorkforceProfileOFOCodeSummaryChart = (props: IProps) => {

    const history = useHistory();
    const WSPScorecard = useRecoilValue(WSPScorecardAtom);

    const [data, setData] = React.useState<WorkforceProfileOFOCodeSummary[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);

    const [showModal, setShowModal] = React.useState(false);
    const [selectedOfoCodeCategoryId, setSelectedOfoCodeCategoryId] = React.useState(-1);
    const [selectedOfoCodeYear, setSelectedOfoCodeYear] = React.useState("");
    const [title, setTitle] = React.useState("");

    const totalAfricanMale = data.reduce((accumulator, item) => accumulator + item.headcount.africanMales, 0);
    const totalAfricanFemale = data.reduce((accumulator, item) => accumulator + item.headcount.africanFemales, 0);

    const totalColouredMale = data.reduce((accumulator, item) => accumulator + item.headcount.colouredMales, 0);
    const totalColouredFemale = data.reduce((accumulator, item) => accumulator + item.headcount.colouredFemales, 0);

    const totalIndianMale = data.reduce((accumulator, item) => accumulator + item.headcount.indianMales, 0);
    const totalIndianFemale = data.reduce((accumulator, item) => accumulator + item.headcount.indianFemales, 0);

    const totalWhiteMale = data.reduce((accumulator, item) => accumulator + item.headcount.whiteMales, 0);
    const totalWhiteFemale = data.reduce((accumulator, item) => accumulator + item.headcount.whiteFemales, 0);

    const totalNonSAMale = data.reduce((accumulator, item) => accumulator + item.headcount.nonSAMales, 0);
    const totalNonSAFemale = data.reduce((accumulator, item) => accumulator + item.headcount.nonSAFemales, 0);

    const totalHeacount = data.reduce((accumulator, item) => accumulator + item.totalHeadcount, 0);

    const [lastPopulatedDate, setLastPopulatedDate] = React.useState<Date | null>(null);
    const formattedDate: string = moment(lastPopulatedDate).format('DD MMMM YYYY HH:mm');

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const workforceProfileOFOCodeSummaryPromise = WSPDashboardService.GetWorkforceProfileOFOCodeSummary(props.scorecardId!);
            const dateLastPopulatedPromise = WorkforceProfileService.getDateLastPopulated(props.scorecardId!);

            const [workforceProfileOFOCodeSummaryResponse, dateLastPopulatedResponse] = await Promise.all([workforceProfileOFOCodeSummaryPromise, dateLastPopulatedPromise]);
            setData(workforceProfileOFOCodeSummaryResponse.data);
            setLastPopulatedDate(dateLastPopulatedResponse.data);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (props.scorecardId) {
            fetchData();
        }
    }, [props.scorecardId])

    const openModal = (categoryId: number, ofoYear: string, title: string) => {
        setSelectedOfoCodeCategoryId(categoryId);
        setSelectedOfoCodeYear(ofoYear);
        setTitle(title);
        setShowModal(true);
    }

    const closeModal = () => {
        setShowModal(false);
        setSelectedOfoCodeCategoryId(-1);
        setSelectedOfoCodeYear("");
        setTitle("");
    }

    const generateTableHeadCell = (headerText: JSX.Element) => {
        return <th><Typography variant='body2' fontWeight="bolder" ml={'7px'}>{headerText}</Typography></th>
    }

    const generateTableBodyCell = (value: number | string) => {
        return <td><Typography variant='body2' ml={'7px'}>{value}</Typography></td>
    }

    const handleRouting = () => {
        const workforceProfileUrl = `/wsp/scorecard/${WSPScorecard.wspScorecardId}/WorkforceProfile/previewReport`
        history.push(workforceProfileUrl);
    };

    return (
        <Box mt={2} width={'100%'}>
            {
                isLoading ? <SmallLoading /> :
                    <>
                        {
                            data.length > 0 ?
                                <>
                                    <table style={{ width: '100%' }}>
                                        <thead>
                                            <tr style={{ background: "var(--semantics-disabled, #E9ECEF)", height: '50px' }}>
                                                {generateTableHeadCell(<>OFO Codes <br /> Major Group</>)}
                                                {generateTableHeadCell(<>African <br /> Male</>)}
                                                {generateTableHeadCell(<>African <br /> Female</>)}
                                                {generateTableHeadCell(<>Coloured <br /> Male</>)}
                                                {generateTableHeadCell(<>Coloured <br /> Female</>)}
                                                {generateTableHeadCell(<>Indian <br /> Male</>)}
                                                {generateTableHeadCell(<>Indian <br /> Female</>)}
                                                {generateTableHeadCell(<>White <br /> Male</>)}
                                                {generateTableHeadCell(<>White <br /> Female</>)}
                                                {generateTableHeadCell(<>NON RSA <br /> Male</>)}
                                                {generateTableHeadCell(<>NON RSA <br /> Female</>)}
                                                {generateTableHeadCell(<>Total</>)}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {data.map((item, index) => (
                                                <tr key={index} style={{ borderBottom: '1px solid #C9C9C980', padding: '10px 0 10px 0', height: '50px' }}>
                                                    <td><Typography component="a" onClick={() => openModal(item.categoryId, item.ofoYear, item.label)} variant='body2' ml={'7px'} sx={{ cursor: "pointer" }}>{item.label}</Typography></td>
                                                    {generateTableBodyCell(item.headcount.africanMales)}
                                                    {generateTableBodyCell(item.headcount.africanFemales)}
                                                    {generateTableBodyCell(item.headcount.colouredMales)}
                                                    {generateTableBodyCell(item.headcount.colouredFemales)}
                                                    {generateTableBodyCell(item.headcount.indianMales)}
                                                    {generateTableBodyCell(item.headcount.indianFemales)}
                                                    {generateTableBodyCell(item.headcount.whiteMales)}
                                                    {generateTableBodyCell(item.headcount.whiteFemales)}
                                                    {generateTableBodyCell(item.headcount.nonSAMales)}
                                                    {generateTableBodyCell(item.headcount.nonSAFemales)}
                                                    {generateTableBodyCell(item.totalHeadcount)}
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr style={{ padding: '10px 0 10px 0', height: '50px' }}>
                                                {generateTableBodyCell("TOTAL")}
                                                {generateTableBodyCell(totalAfricanMale)}
                                                {generateTableBodyCell(totalAfricanFemale)}
                                                {generateTableBodyCell(totalColouredMale)}
                                                {generateTableBodyCell(totalColouredFemale)}
                                                {generateTableBodyCell(totalIndianMale)}
                                                {generateTableBodyCell(totalIndianFemale)}
                                                {generateTableBodyCell(totalWhiteMale)}
                                                {generateTableBodyCell(totalWhiteFemale)}
                                                {generateTableBodyCell(totalNonSAMale)}
                                                {generateTableBodyCell(totalNonSAFemale)}
                                                {generateTableBodyCell(totalHeacount)}
                                            </tr>
                                        </tfoot>
                                    </table>

                                    {lastPopulatedDate && (
                                        <Grid item xs={12} textAlign="left" marginTop={2} marginLeft={1}>
                                            <Typography>
                                                <span style={{ fontStyle: 'italic' }}>as at </span>
                                                <span style={{ fontWeight: 'bold' }}>{formattedDate}</span>
                                                <Tooltip
                                                    title={
                                                        <React.Fragment>
                                                            This chart is based on the Workforce Profile Report which was last populated at <strong>{formattedDate}</strong>.
                                                        </React.Fragment>
                                                    }
                                                    arrow
                                                    placement="right"
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: tooltipColour,
                                                                maxWidth: '250px',
                                                                '& .MuiTooltip-arrow': {
                                                                    color: tooltipColour
                                                                },
                                                            },
                                                        },
                                                    }}>
                                                    <IconButton
                                                        size="small"
                                                        edge="end"
                                                        color="inherit"
                                                        aria-label="info"
                                                        style={{ marginTop: '-2px' }}
                                                    >
                                                        <InfoOutlinedIcon style={{ color: formControlColour }} />
                                                    </IconButton >
                                                </Tooltip>
                                            </Typography>
                                        </Grid>
                                    )}

                                    {showModal &&
                                        <WorkforceProfileOFOCodeSummaryModal
                                            ofoCodeCategoryId={selectedOfoCodeCategoryId}
                                            ofoCodeYear={selectedOfoCodeYear}
                                            scorecardId={props.scorecardId!}
                                            title={title}
                                            showModal={showModal}
                                            closeModal={() => closeModal()}
                                        />
                                    }
                                </>
                                :
                                <Box style={{ textAlign: 'center', paddingBottom: '20px' }}>
                                    <img
                                        src={`${process.env.PUBLIC_URL}/img/verificationProcess/Elements/NoData.svg`}
                                        alt={'No Data'}
                                        style={{ maxWidth: '25%', marginTop: '10px' }}
                                    />
                                    <Box>
                                        <Typography variant="h4" mt={2} mb={1}>
                                            Oh dear!
                                        </Typography>
                                        <Typography variant="body2" color="secondary" component="span">
                                            <p>It seems like your Workforce Profile Report is empty. <br /> Please take a moment to ensure that your Workforce Profle Report is populated.</p>
                                        </Typography>
                                        <Button variant='contained' onClick={handleRouting}>
                                            Go to Workforce Profile Report
                                        </Button>
                                    </Box>
                                </Box>
                        }
                    </>
            }
        </Box>
    )
}
